import {useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {MunicipioPicker} from '@/components/municipio-picker'
import {EmployeeSingleSelect} from '@/components/employee-single-select'
import {useMunicipio, type Municipio} from '@/hooks/salud/perfil_riesgo/use-perfil'
import {useAllEmployees, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {
  useCreateSede,
  useSedeDetail,
  useUpdateSedeDetail,
  type SedeCreate,
  type SedeDetail,
  type SedeDetailFields,
} from '@/hooks/empresa/organizacion/use-tags'
import {translateApiError} from '@/lib/errors'
import {validatePhone, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

// Mirrors ARL_OPTIONS in core/validation.py.
const ARL_OPTIONS = [
  'Positiva',
  'Sura',
  'Colmena',
  'Bolívar',
  'AXA Colpatria',
  'La Equidad',
  'Liberty',
  'Mapfre',
  'Previsora',
  'Alfa',
] as const

type FormState = {
  name: string
  direccion: string
  telefono: string
  numTrabajadores: string
  arl: string
  municipio: Municipio | null
  responsableId: string | null
}

const EMPTY_FORM: FormState = {
  name: '',
  direccion: '',
  telefono: '',
  numTrabajadores: '',
  arl: '',
  municipio: null,
  responsableId: null,
}

function toForm(detail: SedeDetail, municipio: Municipio | undefined): FormState {
  return {
    name: detail.name,
    direccion: detail.direccion ?? '',
    telefono: detail.telefono ?? '',
    numTrabajadores: detail.num_trabajadores?.toString() ?? '',
    arl: detail.arl ?? '',
    municipio: municipio ?? null,
    responsableId: detail.responsable_employee_id ?? null,
  }
}

export function SedeDetailDialog({
  businessId,
  sedeId,
  open,
  onOpenChange,
}: {
  businessId: string
  /** `null` creates a new sede. */
  sedeId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const isEdit = sedeId !== null
  const detail = useSedeDetail(businessId, open ? (sedeId ?? undefined) : undefined)
  const {data: currentMunicipio, isLoading: municipioLoading} = useMunicipio(
    businessId,
    open ? detail.data?.municipio_code : undefined
  )
  const {data: roster} = useAllEmployees(businessId)
  // La caché puede traer la sede de antes del último guardado: el formulario nace de la lectura de esta apertura.
  const loading = isEdit && (!detail.isFetchedAfterMount || municipioLoading)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('business.sedes.editTitle') : t('business.sedes.newTitle')}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <SedeDetailForm
            businessId={businessId}
            sedeId={sedeId}
            detail={detail.data}
            currentMunicipio={currentMunicipio}
            roster={roster ?? []}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Vive dentro de `DialogContent`, que se desmonta al cerrar: cada apertura arranca del estado inicial. */
function SedeDetailForm({
  businessId,
  sedeId,
  detail,
  currentMunicipio,
  roster,
  onOpenChange,
}: {
  businessId: string
  sedeId: string | null
  detail: SedeDetail | undefined
  currentMunicipio: Municipio | undefined
  roster: Employee[]
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const isEdit = sedeId !== null
  const create = useCreateSede(businessId)
  const update = useUpdateSedeDetail(businessId, sedeId ?? '')
  const [form, setForm] = useState<FormState>(() => (detail ? toForm(detail, currentMunicipio) : EMPTY_FORM))
  const [nameError, setNameError] = useState<string | undefined>()
  const [telefonoError, setTelefonoError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const responsable = roster.find((e) => e.id === form.responsableId) ?? null

  function blurName(e: FocusEvent<HTMLInputElement>) {
    if (!isEdit) setNameError(validateRequired(e.target.value) ?? undefined)
  }

  function blurTelefono(e: FocusEvent<HTMLInputElement>) {
    const raw = e.target.value.trim()
    setTelefonoError((raw ? validatePhone(raw) : null) ?? undefined)
  }

  function detailFields(): SedeDetailFields {
    return {
      direccion: form.direccion.trim() || null,
      municipio_code: form.municipio?.code ?? null,
      telefono: form.telefono.trim() || null,
      num_trabajadores: form.numTrabajadores.trim() ? Number(form.numTrabajadores) : null,
      responsable_employee_id: responsable?.id ?? null,
      arl: form.arl.trim() || null,
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const telefonoErr = form.telefono.trim() ? validatePhone(form.telefono) : null
    setTelefonoError(telefonoErr ?? undefined)
    if (!isEdit) {
      const err = validateRequired(form.name)
      setNameError(err ?? undefined)
      if (err) return
    }
    if (telefonoErr) return
    setError(null)
    if (isEdit) {
      update.mutate(detailFields(), {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(translateApiError(err)),
      })
    } else {
      const payload: SedeCreate = {name: form.name.trim(), ...detailFields()}
      create.mutate(payload, {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(translateApiError(err)),
      })
    }
  }

  const pending = isEdit ? update.isPending : create.isPending

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="sede-detail-form" noValidate>
      <div className="space-y-2">
        <Label htmlFor="sede-name">{t('business.sedes.fields.name')}</Label>
        <Input
          id="sede-name"
          value={form.name}
          disabled={isEdit}
          maxLength={tope('ORG')}
          required
          data-testid="sede-name-input"
          onChange={(e) => {
            setForm((f) => ({...f, name: e.target.value}))
            setNameError(undefined)
          }}
          onBlur={blurName}
          aria-invalid={!!nameError}
        />
        <FieldError error={nameError} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sede-direccion">{t('business.sedes.fields.direccion')}</Label>
        <Input
          id="sede-direccion"
          value={form.direccion}
          maxLength={tope('DIR')}
          data-testid="sede-direccion-input"
          onChange={(e) => setForm((f) => ({...f, direccion: e.target.value}))}
        />
      </div>
      <div className="space-y-2">
        <Label>{t('business.sedes.fields.municipio')}</Label>
        <MunicipioPicker
          businessId={businessId}
          value={form.municipio}
          disabled={false}
          onPick={(municipio) => setForm((f) => ({...f, municipio}))}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sede-telefono">{t('business.sedes.fields.telefono')}</Label>
        <Input
          id="sede-telefono"
          value={form.telefono}
          maxLength={tope('TEL')}
          data-testid="sede-telefono-input"
          aria-invalid={!!telefonoError}
          onChange={(e) => {
            setForm((f) => ({...f, telefono: e.target.value}))
            setTelefonoError(undefined)
          }}
          onBlur={blurTelefono}
        />
        <FieldError error={telefonoError} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sede-num-trabajadores">{t('business.sedes.fields.numTrabajadores')}</Label>
        <Input
          id="sede-num-trabajadores"
          type="number"
          min={0}
          value={form.numTrabajadores}
          data-testid="sede-num-trabajadores-input"
          onChange={(e) => setForm((f) => ({...f, numTrabajadores: e.target.value}))}
        />
      </div>
      <div className="space-y-2">
        <Label>{t('business.sedes.fields.responsable')}</Label>
        <EmployeeSingleSelect
          roster={roster}
          value={responsable}
          testId="sede-responsable-picker"
          onChange={(employee) => setForm((f) => ({...f, responsableId: employee?.id ?? null}))}
        />
      </div>
      <CatalogOtherField
        id="sede-arl"
        label={t('business.sedes.fields.arl')}
        options={ARL_OPTIONS}
        value={form.arl}
        onChange={(arl) => setForm((f) => ({...f, arl}))}
        onCommit={(arl) => setForm((f) => ({...f, arl}))}
      />
      <FormDialogFooter
        error={error}
        pending={pending}
        submitLabel={isEdit ? t('common.save') : t('business.sedes.create')}
        onCancel={() => onOpenChange(false)}
      />
    </form>
  )
}
